import type { WEIString, } from "@bracket/sharedTypes";
import { logger, } from "@bracket/ui";
const url = "https://api.morpho.org/graphql";



export const query = async <K extends keyof typeof ConfiguredQueries>(
	query: K,
	variables: (typeof ConfiguredQueries)[K]["input"],
): Promise<(typeof ConfiguredQueries)[K]["resultShape"] | null> => {

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			query: ConfiguredQueries[query].query,
			variables,
		}),
	});
	const { data, errors } = await response.json();
	if (errors) {
		// Handle "not found" errors by returning null instead of throwing
		const isNotFound = errors.some((error: { status?: string, message?: string, }) => 
			error.status === "NOT_FOUND" || 
			error.message?.includes("No results matching given parameters")
		);
		
		if (isNotFound) {
			return null;
		}
		
		console.error("GraphQL errors:", JSON.stringify(errors, null, 2));
		throw new Error(`GraphQL query failed: ${errors[0]?.message || "Unknown error"}`);
	}
	
	if (!response.ok) {
		throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
	}
	
	if (data === null) {
		return null;
	}
	
	return ConfiguredQueries[query].outMap.reduce((acc, key) => {
		return acc[key];
	}, data);
};

// pull back on avgBorrowApy for APY
export const ConfiguredQueries = {
	MarketPosition: {
		query: `
			query MarketPosition($userAddress: String!, $marketId: String!, $chainId: Int!) {
				marketPosition(userAddress: $userAddress, marketUniqueKey: $marketId) {
					healthFactor
					priceVariationToLiquidationPrice
					state {
						id
						borrowAssets
						borrowAssetsUsd
						borrowShares
						collateral
						collateralUsd
						timestamp
					}
				}
				marketById(marketId: $marketId, chainId: $chainId) {
					lltv
					state {
						avgBorrowApy
					}
				}
			}
		`,
		input: {
			userAddress: "string",
			marketId: "string",
			chainId: 1,
		},
		resultShape: {
			marketPosition: {
				healthFactor: 1,
				priceVariationToLiquidationPrice: 1,
				state: {
					id: "string",
					borrowAssets: 1,
					borrowAssetsUsd: 1,
					borrowShares: "string",
					collateral: 1,
					collateralUsd: 1,
					timestamp: 1,
				}
			},
			marketById: {
				lltv: "string",
				state: {
					avgBorrowApy: 1,
				}
			},
		} as {
			marketPosition: {
				healthFactor: number | null;
				priceVariationToLiquidationPrice: number | null;
				state: {
					borrowAssets: number;
					borrowAssetsUsd: number;
					borrowShares: WEIString;
					collateral: number;
					collateralUsd: number;
					timestamp: number;
					id: string;
				}
			};
			marketById: {
				lltv: WEIString,
				state: {
					avgBorrowApy: number;
				}
			};
		},
		outMap: [],
	},
	MorphoMarket: {
		query: `
			query MorphoMarket($marketUniqueKey: String!) {
				marketById(uniqueKey: $marketUniqueKey) {
					lltv
					targetBorrowUtilization
					state {
						avgNetBorrowApy
						borrowApy
					}
				}
			}
		`,
		input: {
			marketUniqueKey: "string",
		},
		resultShape: {
			marketById: {
				lltv: "string",
				targetBorrowUtilization: "string",
				state: {
					avgNetBorrowApy: 1,
					borrowApy: 1,
				}
			}
		} as {
			marketById: {
				lltv: WEIString,
				targetBorrowUtilization: WEIString;
				state: {
					avgNetBorrowApy: number;
					borrowApy: number;
				};
			};
		},
		outMap: [],
	},
};
